import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AlertTriangle } from "lucide-react";
import { api, data } from "@/lib/api";
import { Page, Spinner } from "@/components/Layout";
import VetrinaEditor from "@/pages/operatore/VetrinaEditor";
import Riscatto from "@/pages/operatore/Riscatto";
import { Vendite, Movimenti } from "@/pages/operatore/Report";
import { Piano, Incassi } from "@/pages/operatore/Piano";
import Team from "@/pages/operatore/Team";

const G = ["titolare", "gestore"];
const TUTTI = ["titolare", "gestore", "cassa"];
const TABS = [["vetrina", "Vetrina", G], ["riscatto", "Riscatto", TUTTI], ["vendite", "Vendite", G], ["movimenti", "Saldi e movimenti", G],
  ["piano", "Piano spazio", ["titolare"]], ["incassi", "Incassi Stripe", ["titolare"]], ["team", "Team", ["titolare"]]];
const STATI = { in_attesa_pagamento: ["badge-warn", "In attesa di pagamento piano"], pagato_da_completare: ["badge-gold", "Pagato – completa la vetrina"], attivo: ["badge-ok", "Spazio attivo"], scaduto: ["badge-err", "Spazio scaduto"] };
const REV = { bozza: "Bozza", in_revisione: "In revisione", approvata: "Approvata", rifiutata: "Rifiutata" };

export default function OperatoreDashboard() {
  const [sp, setSp] = useSearchParams();
  const [st, setSt] = useState(null);
  const load = useCallback(() => api.get("/operatore/stato").then((r) => setSt(r.data)), []);
  useEffect(() => {
    load();
  }, [load]);
  if (!st) return <Spinner />;
  const op = st.operatore;
  const ruolo = st.ruolo_azienda;
  const tabs = TABS.filter(([, , r]) => r.includes(ruolo));
  const def = ruolo === "cassa" ? "riscatto" : ruolo === "titolare" && !["attivo", "pagato_da_completare"].includes(op.stato) ? "piano" : "vetrina";
  const tab = tabs.some(([k]) => k === sp.get("tab")) ? sp.get("tab") : def;
  const [cls, label] = op.sospeso ? ["badge-err", "Sospeso dall'amministrazione"] : STATI[op.stato] || ["badge-muted", op.stato];

  return (
    <Page>
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <p className="caption">Pannello attività</p>
          <h1 className="font-display text-4xl sm:text-5xl mt-2" data-testid="op-dashboard-title">{op.ragione_sociale}</h1>
        </div>
        <div className="flex flex-wrap gap-2 items-center text-sm">
          <span className={cls} data-testid="op-stato-badge">{label}</span>
          <span className="badge-gold" data-testid="op-vetrina-badge">Vetrina: {REV[st.vetrina.stato_revisione]}</span>
          {st.vetrina.stato_bozza === "modificata" && <span className="badge-warn" data-testid="op-bozza-modificata-badge">Modifiche non inviate</span>}
          {op.scadenza_spazio && <span className="text-[#786E65]" data-testid="op-scadenza">Scadenza spazio: {data(op.scadenza_spazio)}</span>}
        </div>
      </div>
      {op.stato !== "attivo" && ruolo !== "cassa" && (
        <div className="mt-6 flex gap-3 p-4 rounded-xl bg-amber-50 border border-amber-200 text-amber-900 text-sm" data-testid="op-piano-banner">
          <AlertTriangle size={18} className="shrink-0" />
          {op.stato === "scaduto" ? "Il tuo spazio è scaduto: la vetrina è sospesa ma le gift card vendute restano riscattabili. Rinnova il piano per tornare online."
            : op.stato === "pagato_da_completare" ? "Pagamento ricevuto: completa la vetrina (5 immagini e pagina prodotto) e inviala in revisione. Andrà online dopo l'approvazione."
            : "Scegli e paga un piano spazio per poter pubblicare la vetrina."}
        </div>
      )}
      <div className="flex gap-1 overflow-x-auto border-b border-[#E3D9F0] mt-8">
        {tabs.map(([k, l]) => (
          <button key={k} onClick={() => setSp({ tab: k })} data-testid={`op-tab-${k}`}
            className={`px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 -mb-px transition-colors ${tab === k ? "border-[#7E5BAF] text-[#7E5BAF]" : "border-transparent text-[#786E65] hover:text-[#261E1B]"}`}>{l}</button>
        ))}
      </div>
      <div className="mt-8">
        {tab === "vetrina" && <VetrinaEditor st={st} reload={load} />}
        {tab === "riscatto" && <Riscatto />}
        {tab === "vendite" && <Vendite />}
        {tab === "movimenti" && <Movimenti />}
        {tab === "piano" && <Piano st={st} />}
        {tab === "incassi" && <Incassi op={op} reload={load} />}
        {tab === "team" && <Team />}
      </div>
    </Page>
  );
}
